import { API_URL } from '../config/constants';

const STORAGE_KEY = 'workloop_remembered_accounts';
const PREF_KEY = 'workloop_remember_me';
const AVATAR_CACHE_KEY = 'workloop_avatar_cache';
const MAX_ACCOUNTS = 6;
const AVATAR_SIZE = 256;

const isLocalAvatar = (src) => typeof src === 'string' && src.startsWith('data:image');

const readList = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeList = (list) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    try {
      const compact = list.map((item) => ({
        ...item,
        avatarDataUrl: item.avatarDataUrl && item.avatarDataUrl.length > 180000 ? null : item.avatarDataUrl,
      }));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(compact));
    } catch {
      // Ignore quota / private-mode failures
    }
  }
};

const readAvatarCache = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(AVATAR_CACHE_KEY) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeAvatarCache = (cache) => {
  try {
    localStorage.setItem(AVATAR_CACHE_KEY, JSON.stringify(cache));
  } catch {
    try {
      const entries = Object.entries(cache).filter(([, value]) => isLocalAvatar(value));
      localStorage.setItem(AVATAR_CACHE_KEY, JSON.stringify(Object.fromEntries(entries.slice(0, 8))));
    } catch {
      // Ignore quota / private-mode failures
    }
  }
};

export const getCachedAvatar = (...keys) => {
  const cache = readAvatarCache();
  for (const key of keys) {
    if (key && isLocalAvatar(cache[key])) return cache[key];
  }
  return null;
};

export const putCachedAvatar = (keys, dataUrl) => {
  if (!isLocalAvatar(dataUrl)) return;
  const cache = readAvatarCache();
  (Array.isArray(keys) ? keys : [keys]).filter(Boolean).forEach((key) => {
    cache[String(key)] = dataUrl;
  });
  writeAvatarCache(cache);
};

export const resolveRememberedAvatar = (account) => {
  if (!account) return null;
  if (isLocalAvatar(account.avatarDataUrl)) return account.avatarDataUrl;
  return getCachedAvatar(account.username, account.profilePicture);
};

export const isRememberMePreferred = () => localStorage.getItem(PREF_KEY) === 'true';

export const setRememberMePreferred = (value) => {
  localStorage.setItem(PREF_KEY, value ? 'true' : 'false');
};

export const getRememberedAccounts = () => {
  const list = readList();
  return list.map((item) => {
    const avatar = resolveRememberedAvatar(item) || (isLocalAvatar(item.avatarDataUrl) ? item.avatarDataUrl : null);
    if (avatar) {
      putCachedAvatar([item.username, item.profilePicture], avatar);
    }
    return {
      ...item,
      avatarDataUrl: avatar,
    };
  });
};

const realmRememberedApi = () => {
  try {
    return typeof window !== 'undefined' ? window.workloopRealm?.remembered : null;
  } catch {
    return null;
  }
};

const persistListToRealm = (list) => {
  const api = realmRememberedApi();
  if (!api) return;
  const accounts = Array.isArray(list) ? list : [];
  if (api.replaceAll) {
    api.replaceAll(accounts).catch(() => {
      if (api.save) {
        accounts.forEach((account) => api.save(account).catch(() => {}));
      }
    });
    return;
  }
  if (api.save) {
    accounts.forEach((account) => api.save(account).catch(() => {}));
  }
};

const mergeAccounts = (local, remote) => {
  const map = new Map();
  [...(remote || []), ...(local || [])].forEach((item) => {
    if (!item?.username) return;
    const prev = map.get(item.username);
    if (!prev) {
      map.set(item.username, { ...item });
      return;
    }
    map.set(item.username, {
      ...prev,
      ...item,
      email: item.email || prev.email || '',
      avatarDataUrl: isLocalAvatar(item.avatarDataUrl)
        ? item.avatarDataUrl
        : (isLocalAvatar(prev.avatarDataUrl) ? prev.avatarDataUrl : null),
      savedAt: Math.max(Number(prev.savedAt) || 0, Number(item.savedAt) || 0),
    });
  });
  return Array.from(map.values())
    .sort((a, b) => (Number(b.savedAt) || 0) - (Number(a.savedAt) || 0))
    .slice(0, MAX_ACCOUNTS);
};

export const hydrateRememberedAccounts = async () => {
  const local = getRememberedAccounts();
  const api = realmRememberedApi();
  if (!api?.list) return local;
  try {
    const remote = await api.list();
    const merged = mergeAccounts(local, remote);
    writeList(merged);
    persistListToRealm(merged);
    merged.forEach((item) => {
      if (isLocalAvatar(item.avatarDataUrl)) {
        putCachedAvatar([item.username, item.profilePicture], item.avatarDataUrl);
      }
    });
    return merged;
  } catch {
    return local;
  }
};

const displayNameOf = (user) => {
  const full = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return full || user.username || 'User';
};

const blobToAvatarDataUrl = (blob) => new Promise((resolve) => {
  const reader = new FileReader();
  reader.onload = () => {
    const src = typeof reader.result === 'string' ? reader.result : null;
    if (!src) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = AVATAR_SIZE;
        canvas.height = AVATAR_SIZE;
        const ctx = canvas.getContext('2d');
        const min = Math.min(img.width, img.height) || AVATAR_SIZE;
        const sx = (img.width - min) / 2;
        const sy = (img.height - min) / 2;
        ctx.drawImage(img, sx, sy, min, min, 0, 0, AVATAR_SIZE, AVATAR_SIZE);
        resolve(canvas.toDataURL('image/jpeg', 0.82));
      } catch {
        resolve(isLocalAvatar(src) ? src : null);
      }
    };
    img.onerror = () => resolve(isLocalAvatar(src) ? src : null);
    img.src = src;
  };
  reader.onerror = () => resolve(null);
  reader.readAsDataURL(blob);
});

export const cacheAvatarFromElement = (keys, imgEl) => {
  if (!imgEl || !imgEl.naturalWidth) return;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = AVATAR_SIZE;
    canvas.height = AVATAR_SIZE;
    const ctx = canvas.getContext('2d');
    const min = Math.min(imgEl.naturalWidth, imgEl.naturalHeight) || AVATAR_SIZE;
    const sx = (imgEl.naturalWidth - min) / 2;
    const sy = (imgEl.naturalHeight - min) / 2;
    ctx.drawImage(imgEl, sx, sy, min, min, 0, 0, AVATAR_SIZE, AVATAR_SIZE);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.82);
    putCachedAvatar(keys, dataUrl);
    const keyList = Array.isArray(keys) ? keys : [keys];
    const next = readList().map((item) => (
      keyList.includes(item.username) ? { ...item, avatarDataUrl: dataUrl } : item
    ));
    writeList(next);
    persistListToRealm(next);
  } catch {
    // Cross-origin images cannot be snapshotted
  }
};

const isUsableAvatarBlob = (blob) => {
  if (!blob || !blob.size || blob.size < 32) return false;
  const type = String(blob.type || '').toLowerCase();
  if (type.includes('json') || type.includes('html') || type.includes('text')) return false;
  return type.startsWith('image/') || type === 'application/octet-stream' || !type;
};

const fetchAvatarBlob = async (fileKey) => {
  const { buildFileFetchUrl, buildLegacyFileUrl, getPresignedFileUrl } = await import('./fileUrlService');
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
  const urls = [buildFileFetchUrl(fileKey), buildLegacyFileUrl(fileKey)].filter(Boolean);
  try {
    const presigned = await getPresignedFileUrl(fileKey);
    if (presigned && !urls.includes(presigned)) urls.push(presigned);
  } catch {
    // continue with API URLs
  }

  for (const url of urls) {
    try {
      const isApiUrl = url.includes('/files/') || url.startsWith('/') || url.includes(API_URL);
      const response = await fetch(url, {
        headers: isApiUrl ? authHeaders : {},
        credentials: 'omit',
      });
      if (!response.ok) continue;
      const blob = await response.blob();
      if (isUsableAvatarBlob(blob)) return blob;
    } catch {
      // try next URL
    }
  }
  return null;
};

export const ensureAvatarCached = async (fileKey, username) => {
  const existing = getCachedAvatar(fileKey, username);
  if (existing) return existing;
  if (!fileKey) return null;
  const snapshot = await snapshotAvatar(fileKey);
  if (!isLocalAvatar(snapshot)) return null;
  putCachedAvatar([fileKey, username], snapshot);
  if (username) {
    const next = readList().map((item) => (
      item.username === username ? { ...item, avatarDataUrl: snapshot, profilePicture: fileKey || item.profilePicture } : item
    ));
    writeList(next);
    persistListToRealm(next);
  }
  return snapshot;
};

const snapshotAvatar = async (fileKey) => {
  if (!fileKey) return getCachedAvatar(fileKey);
  const key = String(fileKey).trim();
  const cached = getCachedAvatar(key);
  if (key.startsWith('data:image')) return key;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return cached;
  }
  try {
    const blob = await fetchAvatarBlob(key);
    if (!blob) return cached;
    return await blobToAvatarDataUrl(blob) || cached;
  } catch {
    return cached;
  }
};

export const saveRememberedAccount = async (user) => {
  if (!user?.username) return;
  const previous = readList().find((item) => item.username === user.username);
  const existingAvatar = resolveRememberedAvatar(previous) || getCachedAvatar(user.username, user.profilePicture);
  const entry = {
    username: user.username,
    email: user.email || previous?.email || '',
    displayName: displayNameOf(user),
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    profilePicture: user.profilePicture || previous?.profilePicture || '',
    avatarDataUrl: existingAvatar || null,
    savedAt: Date.now(),
  };
  writeList([entry, ...readList().filter((item) => item.username !== user.username)].slice(0, MAX_ACCOUNTS));
  persistListToRealm(readList());
  if (existingAvatar) {
    putCachedAvatar([user.username, entry.profilePicture], existingAvatar);
  }

  const pictureKey = user.profilePicture || previous?.profilePicture;
  if (!pictureKey) {
    persistListToRealm(readList());
    return;
  }
  const snapshot = await snapshotAvatar(pictureKey);
  if (!isLocalAvatar(snapshot)) {
    persistListToRealm(readList());
    return;
  }
  putCachedAvatar([user.username, pictureKey], snapshot);
  const latest = readList();
  writeList(latest.map((item) => (
    item.username === user.username
      ? { ...item, avatarDataUrl: snapshot, profilePicture: pictureKey }
      : item
  )));
  persistListToRealm(readList());
};

export const removeRememberedAccount = (username) => {
  writeList(readList().filter((item) => item.username !== username));
  persistListToRealm(readList());
  const api = realmRememberedApi();
  if (api?.remove && username) {
    api.remove(username).catch(() => {});
  }
};
